import { fetchPlaceDetails as fetchPlaceDetailsApi } from "./places-api";

/**
 * Formats a place prediction into a readable address string
 * @param {Object} prediction - Google Places Autocomplete prediction
 * @returns {Object} { primary: string, secondary: string, fullAddress: string, placeId: string }
 */
export function formatPlacePrediction(prediction) {
  if (!prediction) {
    return {
      primary: "",
      secondary: "",
      fullAddress: "",
      placeId: "",
    };
  }

  const placeId = prediction.placeId || "";
  const fullAddress = prediction.text?.text || "";

  let primary = prediction.structuredFormatting?.mainText?.text || "";
  let secondary = prediction.structuredFormatting?.secondaryText?.text || "";

  if (!primary && fullAddress) {
    const parts = fullAddress.split(",").map((p) => p.trim());
    primary = parts[0] || "";
    secondary = parts.slice(1).join(", ") || "";
  }

  return {
    primary,
    secondary,
    fullAddress,
    placeId,
  };
}

/**
 * Gets place details from place_id
 * @param {string} placeId - Google Places place_id
 * @param {string} sessionToken - Session token for billing (optional)
 * @param {AbortSignal} signal - Abort signal for cancelling the request (optional)
 * @returns {Promise<Object>} Place details
 */
export async function getPlaceDetails(
  placeId,
  sessionToken = null,
  signal = null
) {
  return fetchPlaceDetailsApi({
    placeId,
    sessionToken,
    signal,
  });
}

/**
 * Formats place details into a standardized location object
 * @param {Object} placeDetails - Google Places place details
 * @returns {Object} { address: string, coordinates: { lat, lng }, placeId: string, postalCode: string, name: string }
 */
export function formatPlaceDetails(placeDetails) {
  if (!placeDetails) {
    return null;
  }

  let postalCode = "";
  if (
    placeDetails.addressComponents &&
    Array.isArray(placeDetails.addressComponents)
  ) {
    const postalCodeComponent = placeDetails.addressComponents.find(
      (component) => component.types?.includes("postal_code")
    );
    postalCode =
      postalCodeComponent?.longText ||
      postalCodeComponent?.shortText ||
      postalCodeComponent?.longName ||
      postalCodeComponent?.shortName ||
      "";
  }

  // Coordinates are not needed for address display, removed to fix API field mask error
  let coordinates = null;

  let placeId = "";
  if (placeDetails.id) {
    placeId = placeDetails.id.replace(/^places\//, "");
  } else if (placeDetails.place_id) {
    placeId = placeDetails.place_id;
  }

  const name = placeDetails.displayName?.text || placeDetails.name || "";
  const address =
    placeDetails.formattedAddress || placeDetails.formatted_address || "";

  return {
    address,
    coordinates,
    placeId,
    postalCode,
    name,
  };
}
